import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';

// Auth interfaces
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      id: string;
      fullName: string;
      email: string;
      role: string;
      status: 'enabled' | 'disabled';
      lastLogin: string;
      lastLoginRaw: string;
    };
    token: string;
    refreshToken: string;
  };
}

export interface AuthUser {
  id: string;
  fullName: string;
  email: string;
  role: string;
  status: 'enabled' | 'disabled';
  lastLogin: string;
  lastLoginRaw: string;
}

export class AuthService {
  private baseURL: string;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    // Remove /api prefix from endpoint if baseURL already includes it
    let normalizedEndpoint = endpoint;
    if (this.baseURL.endsWith('/api') && endpoint.startsWith('/api/')) {
      normalizedEndpoint = endpoint.substring(4); // Remove '/api' prefix
    } else if (this.baseURL === '/api' && endpoint.startsWith('/api')) {
      normalizedEndpoint = endpoint.startsWith('/api/') ? endpoint.substring(4) : endpoint.substring(3);
    }
    const url = `${this.baseURL}${normalizedEndpoint}`;
    
    const config: RequestInit = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        ...options.headers,
      },
    };

    // Secure logging
    secureLogger.apiRequest(endpoint, options.method || 'POST', options.body);

    try {
      const response = await fetch(url, config);
      
      // Secure response logging
      secureLogger.apiResponse(endpoint, response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // Secure error logging
        secureLogger.apiError(endpoint, errorData);
        
        // Provide user-friendly error messages based on status code
        let userFriendlyMessage = errorData.message;
        
        if (!userFriendlyMessage) {
          switch (response.status) {
            case 401:
              userFriendlyMessage = 'Invalid email or password. Please check your credentials and try again.';
              break;
            case 400:
              userFriendlyMessage = 'Please enter a valid email address and password.';
              break;
            case 500:
              userFriendlyMessage = 'Server error. Please try again later.';
              break;
            case 404:
              userFriendlyMessage = 'Login service not found. Please contact support.';
              break;
            default:
              userFriendlyMessage = `HTTP ${response.status}: ${response.statusText}`;
          }
        }
        
        throw new Error(userFriendlyMessage);
      }

      const data = await response.json();
      // Secure success logging
      secureLogger.apiResponse(endpoint, response.status);
      return data;
    } catch (error) {
      // Secure error logging
      secureLogger.apiError(endpoint, error);
      if (error instanceof Error) {
        // If it's already a user-friendly message, pass it through
        if (error.message.includes('Invalid email or password') || 
            error.message.includes('Please enter a valid') ||
            error.message.includes('Server error') ||
            error.message.includes('Login service not found')) {
          throw error;
        }
        // Handle network errors
        if (error.message.includes('fetch') || error.message.includes('Network')) {
          throw new Error('Unable to connect to the server. Please check your internet connection.');
        }
        throw error;
      }
      throw new Error('An unexpected error occurred');
    }
  }

  // Admin login is public and protected server-side by strict rate limiting.
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    // baseURL already includes /api, so don't add it again
    // Overridable via REACT_APP_LOGIN_ENDPOINT for local backends with a different route
    const endpoint = process.env.REACT_APP_LOGIN_ENDPOINT || '/admin/users/login';
    return this.makeRequest<LoginResponse>(endpoint, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  // Test API connection
  async testConnection(): Promise<boolean> {
    try {
      // Connection test logging removed for security
      return true;
    } catch (error) {
      console.error('❌ Auth API connection failed:', error);
      return false;
    }
  }
}

// Create singleton instance
export const authService = new AuthService();
